import { apiService } from './apiService';

export interface PresignedUrlResponse {
  asset_id: string;
  asset_sk: string;
  filename: string;
  content_type: string;
  s3_key: string;
  presigned_url: string;
  presigned_fields: Record<string, string>;
  expires_in: number;
}

export interface PresignedUrlsResponse {
  message: string;
  uploads: PresignedUrlResponse[];
  bucket: string;
}

export interface SubmitAssetsResponse {
  message: string;
  submitted_assets: Array<{
    asset_id: string;
    filename: string;
    status: string;
    file_url: string;
  }>;
  failed_assets: Array<{
    asset_id: string;
    filename: string;
    error: string;
  }>;
}

export interface ViewUrlsResponse {
  message: string;
  urls: Array<{
    asset_sk: string;
    asset_id: string;
    filename: string;
    content_type: string;
    file_size: number;
    presigned_url: string;
    expires_in: number;
  }>;
  failed: Array<{
    asset_id: string;
    error: string;
  }>;
  success_count: number;
  failed_count: number;
}

export interface RemoveAssetsResponse {
  message: string;
  removed_assets: Array<{
    asset_id: string;
    filename: string;
    status: string;
  }>;
  failed_assets: Array<{
    asset_id: string;
    filename: string;
    error: string;
  }>;
}

class FileService {
  /**
   * Get presigned URLs for file uploads
   */
  async getPresignedUrls(filenames: string[]): Promise<PresignedUrlsResponse> {
    return await apiService.post<PresignedUrlsResponse>('/api/files/presigned-urls', { filenames });
  }

  /**
   * Upload file to S3 using presigned URL
   */
  async uploadToS3(file: File, presignedData: PresignedUrlResponse): Promise<boolean> {
    try {
      const formData = new FormData();
      
      // Check if presigned_fields exists before trying to use it
      if (!presignedData.presigned_fields) {
        console.error('presigned_fields is missing from presignedData');
        return false;
      }
      
      // Add all the required fields from the presigned URL
      Object.entries(presignedData.presigned_fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      
      // Add the file last
      formData.append('file', file);

      const response = await fetch(presignedData.presigned_url, {
        method: 'POST',
        body: formData,
      });

      return response.ok;
    } catch (error) {
      console.error('Error uploading to S3:', error);
      return false;
    }
  }

  /**
   * Submit uploaded assets to mark them as active
   */
  async submitAssets(assetSKs: string[]): Promise<SubmitAssetsResponse> {
    return await apiService.put<SubmitAssetsResponse>('/api/files/submit', { asset_sks: assetSKs });
  }

  /**
   * Get view URLs for displaying assets
   */
  async getViewUrls(assetSKs: string[]): Promise<ViewUrlsResponse> {
    return await apiService.post<ViewUrlsResponse>('/api/files/view-urls', { asset_sks: assetSKs });
  }

  /**
   * Get public view URLs for displaying assets
   */
  async getPublicViewUrls(assetSKs: string[]): Promise<ViewUrlsResponse> {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.your-backend.com'}/contents/view-urls`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ asset_sks: assetSKs }),
      });

      if (!response.ok) {
        throw new Error(`Failed to get public view URLs: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting public view URLs:', error);
      throw error;
    }
  }

  /**
   * Remove assets (soft delete)
   */
  async removeAssets(assetSKs: string[]): Promise<RemoveAssetsResponse> {
    // Since apiService.delete doesn't support body, we'll use a direct fetch
    // This is a temporary workaround until apiService supports DELETE with body
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.your-backend.com'}/api/files`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          // Note: Authorization header would need to be added here
          // This is a simplified implementation
        },
        body: JSON.stringify({ asset_sks: assetSKs }),
      });

      if (!response.ok) {
        throw new Error(`Failed to remove assets: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing assets:', error);
      throw error;
    }
  }

  /**
   * Complete upload flow: get presigned URLs, upload to S3, and submit
   */
  async uploadFiles(files: File[]): Promise<string[]> {
    try {
      // Step 1: Get presigned URLs
      const filenames = files.map(file => file.name);

      const presignedResponse = await this.getPresignedUrls(filenames);
      
      // Step 2: Upload files to S3
      const uploadPromises = files.map(async (file, index) => {
        const presignedData = presignedResponse.uploads[index];
        const success = await this.uploadToS3(file, presignedData);
        return { success, assetSK: presignedData.asset_sk }; // Use asset_sk for the SK
      });

      const uploadResults = await Promise.all(uploadPromises);
      const successfulAssetSKs = uploadResults
        .filter(result => result.success)
        .map(result => result.assetSK);

      if (successfulAssetSKs.length === 0) {
        throw new Error('No files were uploaded successfully');
      }

      // Step 3: Submit assets to mark as active
      await this.submitAssets(successfulAssetSKs);
      
      // Return asset SKs (these are what we need for getViewUrls API)
      return successfulAssetSKs;
    } catch (error) {
      console.error('Error in upload flow:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const fileService = new FileService();
export default fileService;